/**
 * Maps technical errors to user-friendly messages.
 * Internal details are logged but not shown to users.
 */

// Note: Keys with special characters need quotes, others are unquoted per ESLint
const ERROR_MESSAGES: Record<string, string> = {
  // Auth errors
  'Invalid login credentials': 'Invalid email or password. Please try again.',
  'Email not confirmed': 'Please verify your email before logging in.',
  'User already registered': 'An account with this email already exists.',
  'Invalid Refresh Token': 'Your session has expired. Please log in again.',
  'Signup disabled': 'Sign up is currently disabled. Please try again later.',
  'Email rate limit exceeded': 'Too many attempts. Please try again later.',
  'Password should be at least': 'Password must be at least 6 characters.',
  'New password should be different': 'New password must be different from your current password.',

  // Database errors
  'duplicate key': 'This item already exists.',
  'foreign key violation': 'This operation cannot be completed due to related data.',
  'not found': 'The requested item was not found.',
  PGRST116: 'The requested item was not found.',
  23503: 'This operation cannot be completed because it references data that no longer exists.',
  23505: 'This item already exists.',
  23514: 'The data provided does not meet the required constraints.',

  // Network errors
  'Failed to fetch': 'Network error. Please check your connection.',
  timeout: 'The request timed out. Please try again.',
  NetworkError: 'Network error. Please check your connection.',
  AbortError: 'The request was cancelled. Please try again.',

  // Stripe errors
  'No Stripe customer found': 'Please set up your payment information first.',
  card_declined: 'Your card was declined. Please try a different payment method.',
  expired_card: 'Your card has expired. Please use a different card.',
  incorrect_cvc: 'The security code is incorrect. Please check and try again.',
  processing_error: 'An error occurred while processing your card. Please try again.',
  rate_limit: 'Too many requests. Please wait a moment and try again.',

  // Permission errors
  Unauthorized: 'You do not have permission to perform this action.',
  Forbidden: 'Access denied.',
  'Not a member': 'You are not a member of this organization.',

  // File upload errors
  'File too large': 'The file is too large. Please choose a smaller file.',
  'Invalid file type': 'This file type is not supported.',

  // Default
  default: 'An unexpected error occurred. Please try again.',
};

interface ErrorData {
  message?: string;
  error?: string;
  code?: string;
}

/**
 * Gets a user-friendly error message from a technical error.
 * Logs the full error for debugging purposes.
 *
 * @param error - The error to convert (can be Error, string, or other types)
 * @param options - Optional configuration
 * @returns A user-friendly error message
 */
export function getUserFriendlyError(
  error: Error | string | ErrorData | null | undefined,
  options?: {
    logError?: boolean;
    context?: string;
  }
): string {
  const { logError = true, context } = options ?? {};

  // Extract the error message
  let errorMessage: string;
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else if (error && typeof error === 'object') {
    // Handle Supabase/PostgreSQL error objects
    const errorObj = error;
    errorMessage = errorObj.message ?? errorObj.error ?? errorObj.code ?? JSON.stringify(error);
  } else {
    errorMessage = 'Unknown error';
  }

  // Log full error for debugging (in development or to error service)
  if (logError) {
    const contextPrefix = context ? `[${context}] ` : '';
    console.error(`${contextPrefix}Full error:`, error);
  }

  // Find matching user-friendly message
  const lowerMessage = errorMessage.toLowerCase();
  for (const [key, message] of Object.entries(ERROR_MESSAGES)) {
    if (key === 'default') continue;
    if (lowerMessage.includes(key.toLowerCase())) {
      return message;
    }
  }

  return ERROR_MESSAGES.default;
}

/**
 * Checks if we're in a development environment.
 * Works in both Node.js and browser contexts.
 */
export function isDevelopment(): boolean {
  // Check Node.js environment
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) {
    return process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';
  }

  // Check browser environment
  if (typeof window !== 'undefined') {
    return window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }

  return false;
}

/**
 * Gets error message for display. Always returns a user-friendly message
 * and logs the full error for debugging.
 */
export function getDisplayError(
  error: Error | string | ErrorData | null | undefined
): string {
  return getUserFriendlyError(error, { logError: true });
}

/**
 * Type guard to check if an error has a message property
 */
export function isErrorWithMessage(error: Error | string | ErrorData | null | undefined): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  );
}
